import React from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBoxOpen,
    faCopy,
    faDrumstickBite,
    faEye,
    faImage,
    faListOl,
    faStickyNote,
    faTruck,
    faUserClock,
    faBoxesStacked,
} from "@fortawesome/free-solid-svg-icons";

import AssignmentCell from "components/task-list/components/task-cells/assignment-cell";
import NoteButton from "components/notes/note-button";
import ThreeWayCheckbox from "components/form-inputs/custom/three-way-checkbox";

import { imDropboxContentLink } from "shared/digital-content";
import { Checkbox, Input, Select } from "components/form-inputs/plain-inputs";
import { WithIcon, WithTooltip } from "shared/table-helpers";
import { DATE_FNS_LONG_DATE_FORMAT, DATE_FNS_SHORT_DATE_FORMAT } from "shared/constants";
import { DueDateFilterButton } from "components/table/components/dueDateFilterButton";
import { PRIORITY_LEVELS } from "admin/assignment/lib/constants";
import { SHIPMENT_FILTER_TYPES, SHIPMENT_TYPE_MAP } from "components/shipments/lib/constants";
import { TaskStatus } from "shared/constants/task-status";
import { format } from "date-fns";
import { approveRejectFilterStrategy } from "./utils";
import { TaskType as TaskTypeMap, taskTypeToLabelMap } from "../../../shared/taskTypeMap";
import { getFormattedDuration } from "../../../shared/calculate-duration";
import { convertToTimeZone } from "date-fns-timezone";

export const MAX_TASKS_ROWS = 4;

export const availableTaskOptions = [
    {
        text: taskTypeToLabelMap[TaskTypeMap.PHOTOGRAPHY],
        value: TaskTypeMap.PHOTOGRAPHY,
    },
    {
        text: taskTypeToLabelMap[TaskTypeMap.PROFILING],
        value: TaskTypeMap.PROFILING,
    },
    {
        text: taskTypeToLabelMap[TaskTypeMap.MEASURING],
        value: TaskTypeMap.MEASURING,
    },
    {
        text: taskTypeToLabelMap[TaskTypeMap.CODING_QA],
        value: TaskTypeMap.CODING_QA,
    },
    {
        text: taskTypeToLabelMap[TaskTypeMap.IMAGE_EDITING_QA],
        value: TaskTypeMap.IMAGE_EDITING_QA,
    },
];

export const DEFAULT_SORT = [
    {
        id: "state",
        desc: TaskStatus.TO_DO,
    },
    {
        id: "due",
        desc: false,
    },
    {
        id: "isRush",
        desc: true,
    },
    {
        id: "priority",
        desc: false,
    },
];

// state is set from server
export const DEFAULT_FILTER = [];

export const SECTION_MAP = {
    measuring: {
        noteKey: "measuring.standard",
        wipId: "measuringWipKey",
    },
    duplicatestaging: {
        noteKey: "",
        label: "Duplicate Staging",
        path: "/duplicate-staging/",
        wipId: "",
    },
    photography: {
        noteKey: "photography.standard",
        label: "Photography",
        path: "/photography/",
        wipId: "photographyWipKey",
    },
    scheduling: {
        noteKey: "scheduling.default",
        label: "Profiling",
        path: "/profiling/",
        wipId: "shipmentKey",
    },
    coding: {
        noteKey: "coding.standard",
        label: "Coding",
        path: "/coding/",
        wipId: "codingWipKey",
    },
    codingqa: {
        noteKey: "coding.standard",
        label: "Coding QA",
        path: "/coding-qa/",
        wipId: "codingWipKey",
    },
    imageediting: {
        noteKey: "imageediting.standard",
        label: "Image Editing",
        path: "/image-editing/",
        wipId: "photographyWipKey",
    },
    imageeditingqa: {
        noteKey: "imageediting.standard",
        label: "Image Editing QA",
        path: "/image-editing-qa/",
        wipId: "photographyWipKey",
    },
    finalqa: {
        noteKey: "finalqa.standard",
        label: "Final QA",
        path: "/final-qa/",
        wipId: "key",
    },
    brandverifiedqa: {
        noteKey: "brandverifiedqa.standard",
        label: "Brand Verified QA",
        path: "/brand-verified-qa/",
        wipId: "key",
    },
};

export const inputFilter = ({ column, filter, onChange }) => {
    return (
        <Input
            disabled={column.disabled}
            name={`filter_${column.id}`}
            value={filter ? filter.value : ""}
            onChange={(event) => onChange(event.target.value)}
        />
    );
};

export const TaskType = {
    IE_QA: "imageeditingqa",
    IE: "imageediting",
    PHOTOGRAPHY: "photography",
    FINAL_QA: "finalqa",
    DUPLICATE_STAGING: "duplicatestaging",
    CODING: "coding",
    CODING_QA: "codingqa",
    BRANDVERIFIEDQA: "brandverifiedqa",
};

export const DEFAULT_COLUMNS = {
    key: { accessor: "context.key", label: "Key", value: true, visibleFor: [TaskType.IE_QA, TaskType.IE, TaskType.PHOTOGRAPHY, TaskType.FINAL_QA] },
    priority: { accessor: "priority", label: "Priority", value: true },
    rush: { accessor: "isRush", label: "Rush", value: true },
    premiumEditing: { accessor: "context.hasPremiumEditing", label: "Premium Editing", value: true, notDefaultFor: [TaskType.BRANDVERIFIEDQA] },
    isFoodService: { accessor: "isFoodService", label: "Foodservice", value: false },
    hasSupplierCodingAttributes: {
        accessor: "context.hasSupplierCodingAttributes",
        label: "Supplier Coding Attributes",
        value: false,
        visibleFor: [TaskType.CODING],
    },
    clientApproval: { accessor: "context.imagesNeedClientApproval", label: "Client Approval", value: false },
    duplicateMatch: { accessor: "context.matchedProductTaskContextKey", label: "Duplicate Match", value: false },
    locationDataSource: { accessor: "context.locationDataSource", label: "Source", value: true, notDefaultFor: [TaskType.BRANDVERIFIEDQA] },
    startDate: { accessor: "shipment.receivedOn", label: "Start Date", value: true, visibleFor: [TaskType.DUPLICATE_STAGING] },
    dueDate: { accessor: "due", label: "Due Date", value: true },
    duration: { accessor: "", label: "Duration", value: true },
    publishDate: { accessor: "context.publishDate", label: "Publish Date", value: true, visibleFor: [TaskType.FINAL_QA] },
    client: { accessor: "client", label: "Client", value: false },
    cxHClient: { accessor: "cxHClient", label: "CES Client", value: true },
    binNumber: { accessor: "context.binTrackingNumber", label: "Bin Id", value: true, notDefaultFor: [TaskType.BRANDVERIFIEDQA] },
    upc: { accessor: "context.upc", label: "UPC", value: true },
    description: { accessor: "description", label: "Task Description", value: true },
    location: { accessor: "locationInfo.location", label: "Location", value: true },
    packageLevelName: { accessor: "context.packageLevelName", label: "Package Level", value: false },
    lastOpened: { accessor: "lastOpenedDateTime", label: "Last Opened", value: true },
    status: { accessor: "state", label: "Status", value: true },
    assignmentGroupKey: { accessor: "assignmentGroupKey", label: "Team Assignment", value: true },
    assignedUserInfo: { accessor: "assignedUserInfo", label: "User Assignment", value: true },
    shipmentType: { accessor: "context.shipmentType", label: "Shipment Type", value: true },
    notes: { accessor: "note", label: "Notes", value: true },
    view: { accessor: "key", label: "View", value: false },
};

export const createColumns = (
    selectedProductsIds,
    selectedAllProduct,
    updateSelectedProduct,
    userColumns,
    onBINClick,
    onNavigateClick,
    locations,
    types,
    assignmentOptions,
    userAssignmentOptions,
    onUpdateTeamAssignments,
    onUpdateUserAssignments,
    sourcePage,
    filterable,
    settings
) => {
    const typesMap =
        types &&
        types.reduce((obj, i) => {
            obj[i.value] = i.text;

            return obj;
        }, {});

    return Object.keys(userColumns).reduce((selectedColumns, key) => {
        if (!userColumns[key].value) {
            return selectedColumns;
        }

        if (userColumns[key].visibleFor && Array.isArray(userColumns[key].visibleFor)) {
            const isColumnHidden = !userColumns[key].visibleFor.includes(sourcePage);

            if (isColumnHidden) {
                return selectedColumns;
            }
        }

        switch (key) {
            case "key":
                if (selectedProductsIds) {
                    selectedColumns.push({
                        Header: (props) => {
                            const filteredProducts = props.data.map((p) => p._original).filter(approveRejectFilterStrategy(sourcePage, settings));
                            const value = selectedProductsIds.length === filteredProducts.length && filteredProducts.length !== 0;

                            return (
                                <Checkbox
                                    name="selected-state"
                                    value={value}
                                    onChange={() => selectedAllProduct(value ? [] : filteredProducts)}
                                />
                            );
                        },
                        accessor: "context.key",
                        filterable: false,
                        sortable: false,
                        Cell: (row) => {
                            const { state, description } = row.original;

                            if (!approveRejectFilterStrategy(sourcePage, settings)({ state, description })) {
                                return null;
                            }

                            return (
                                <Checkbox
                                    classes="-no-margin"
                                    name={`select_${row.value}`}
                                    value={selectedProductsIds.includes(row.value)}
                                    onChange={(e) => updateSelectedProduct(row.original, e.currentTarget.value)}
                                />
                            );
                        },
                        width: 40,
                        className: "txt-center",
                    });
                }

                break;
            case "priority":
                selectedColumns.push({
                    Header: WithIcon("Priority", faListOl),
                    accessor: "priority",
                    Cell: (row) => {
                        return row.value && row.value !== 255 ? row.value : "";
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => onChange(event.target.value)}
                                style={{ width: "100%" }}
                                value={filter ? filter.value : ""}
                                options={[{ text: " ", value: "" }, ...PRIORITY_LEVELS, { text: "≠", value: "null" }]}
                                excludeSelect={true}
                            />
                        );
                    },
                    width: 68,
                    style: { textAlign: "center" },
                });

                break;
            case "rush":
                selectedColumns.push({
                    Header: WithIcon("Rush", faTruck),
                    accessor: "isRush",
                    Cell: (row) => {
                        return row.value ? <FontAwesomeIcon icon={faTruck} /> : "";
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <ThreeWayCheckbox
                                onChange={onChange}
                                value={filter ? filter.value : ""}
                            />
                        );
                    },
                    width: 40,
                });

                break;
            case "premiumEditing":
                selectedColumns.push({
                    Header: WithIcon("Premium Editing", faImage),
                    accessor: "context.hasPremiumEditing",
                    Cell: (row) => {
                        return row.value ? <FontAwesomeIcon icon={faImage} /> : "";
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <ThreeWayCheckbox
                                onChange={onChange}
                                value={filter ? filter.value : ""}
                            />
                        );
                    },
                    width: 40,
                });

                break;
            case "isFoodService":
                selectedColumns.push({
                    Header: WithIcon("Foodservice", faDrumstickBite),
                    accessor: "context.isFoodService",
                    Cell: (row) => {
                        return row.value ? <FontAwesomeIcon icon={faDrumstickBite} /> : "";
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <ThreeWayCheckbox
                                onChange={onChange}
                                value={filter ? filter.value : ""}
                            />
                        );
                    },
                    width: 40,
                });

                break;
            case "hasSupplierCodingAttributes":
                selectedColumns.push({
                    Header: WithIcon("Supplier Coding Attributes", faBoxesStacked),
                    accessor: "context.hasSupplierCodingAttributes",
                    Cell: (row) => {
                        return row.value ? <FontAwesomeIcon icon={faBoxesStacked} /> : "";
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <ThreeWayCheckbox
                                onChange={onChange}
                                value={filter ? filter.value : ""}
                            />
                        );
                    },
                    width: 40,
                });

                break;
            case "duplicateMatch":
                selectedColumns.push({
                    Header: WithIcon("Duplicate Match", faCopy),
                    accessor: userColumns[key].accessor,
                    filterable,
                    Cell: (row) => {
                        return row.value ? <FontAwesomeIcon icon={faCopy} /> : "";
                    },
                    Filter: ({ filter, onChange }) => {
                        return (
                            <ThreeWayCheckbox
                                onChange={onChange}
                                value={filter ? filter.value : ""}
                            />
                        );
                    },
                    width: 40,
                });

                break;
            case "locationDataSource":
                selectedColumns.push({
                    Header: WithTooltip("Source"),
                    accessor: "context.locationDataSource",
                    filterable,
                    Cell: (row) => {
                        return WithTooltip(row.value);
                    },
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => onChange(event.target.value)}
                                style={{ width: "100%" }}
                                excludeSelect={false}
                                value={(filter && filter.value) || ""}
                                options={settings.locationDataSources}
                            />
                        );
                    },
                });

                break;
            case "dueDate":
                selectedColumns.push({
                    Header: WithTooltip("Due Date (Central Time)"),
                    accessor: "due",
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <DueDateFilterButton
                                filterProp={filter}
                                onChangeProp={onChange}
                            />
                        );
                    },
                    Cell: (row) => {
                        const date = convertToTimeZone(row.value, {
                            timeZone: "America/Chicago",
                        });
                        const isOnHold = row.original.state === TaskStatus.WAITING_FOR_CA && sourcePage === TaskType.IE_QA;
                        const formattedDate = row.value
                            ? format(
                                  date,
                                  row.original.showTimeOnDueDate && (sourcePage === TaskType.CODING || sourcePage === TaskType.CODING_QA)
                                      ? DATE_FNS_LONG_DATE_FORMAT
                                      : DATE_FNS_SHORT_DATE_FORMAT
                              )
                            : "";

                        return WithTooltip(isOnHold ? `${formattedDate} (On Hold)` : formattedDate);
                    },
                });

                break;
            case "duration":
                selectedColumns.push({
                    Header: WithTooltip("Duration"),
                    accessor: "",
                    Cell: (row) => {
                        const isOnHold = row.original.state === TaskStatus.WAITING_FOR_CA && sourcePage === TaskType.IE_QA;
                        const formattedDate = getFormattedDuration(row.original.due);

                        return row.original.state !== TaskStatus.COMPLETED
                            ? WithTooltip(isOnHold ? `${formattedDate} (On Hold)` : formattedDate)
                            : "";
                    },
                    filterable: false,
                    sortable: false,
                });
                break;
            case "startDate":
                selectedColumns.push({
                    Header: WithTooltip("Start Date"),
                    accessor: "shipment.startDate",
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <DueDateFilterButton
                                filterProp={filter}
                                onChangeProp={onChange}
                            />
                        );
                    },
                    Cell: (row) => {
                        const dateString = row.value ? format(new Date(row.value), DATE_FNS_LONG_DATE_FORMAT) : "";

                        return WithTooltip(dateString);
                    },
                });

                break;
            case "publishDate":
                selectedColumns.push({
                    Header: WithTooltip("Publish Date"),
                    accessor: "context.publishDate",
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <DueDateFilterButton
                                filterProp={filter}
                                onChangeProp={onChange}
                            />
                        );
                    },
                    Cell: (row) => {
                        const dateString = row.value ? format(new Date(row.value), DATE_FNS_LONG_DATE_FORMAT) : "";

                        return WithTooltip(dateString);
                    },
                });

                break;
            case "binNumber":
                selectedColumns.push({
                    Header: WithTooltip("Bin ID"),
                    accessor: "context.binTrackingNumber",
                    filterable,
                    Filter: inputFilter,
                    Cell: (rowInfo) => {
                        const { shipmentKey } = rowInfo.original.context;
                        const shipmentUrl = `/shipment-editor/${shipmentKey}`;

                        return (
                            <span
                                className="txt-link"
                                onClick={(event) => onBINClick(event, shipmentUrl)}
                                title="Open BIN (ctrl+click)"
                            >
                                {rowInfo.value}
                            </span>
                        );
                    },
                });

                break;
            case "upc":
                selectedColumns.push({
                    Header: WithTooltip("UPC"),
                    accessor: "context.upc",
                    filterable,
                    Filter: inputFilter,
                    Cell: (row) => {
                        const isUpcValid = row.original.context.isUpcValid;
                        const fullUpc = row.original.context.productModifier ? `${row.value}-${row.original.context.productModifier}` : row.value;

                        return (
                            <div
                                style={{ color: isUpcValid ? "#444444" : "#8e2323" }}
                                title={!isUpcValid ? "Improper UPC" : fullUpc}
                            >
                                {fullUpc}
                            </div>
                        );
                    },
                });

                break;
            case "location":
                selectedColumns.push({
                    Header: WithTooltip("Location"),
                    accessor: "locationInfo.location",
                    Cell: (row) => {
                        return row.original.hasDigitalContent &&
                            row.original.locationInfo.companyName === "ItemMaster" &&
                            row.original.locationInfo.location !== "BYOC_Digital" ? (
                            <a
                                href={imDropboxContentLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={row.original.locationInfo.location}
                            >
                                {row.original.locationInfo.location}
                            </a>
                        ) : (
                            WithTooltip(row.original.locationInfo.location)
                        );
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => onChange(event.target.value)}
                                style={{ width: "100%" }}
                                value={filter ? filter.value : ""}
                                options={locations}
                            />
                        );
                    },
                });

                break;
            case "lastOpened":
                selectedColumns.push({
                    Header: WithTooltip("Last Opened"),
                    accessor: "lastOpenedDateTime",
                    filterable: false,
                    Filter: inputFilter,
                    Cell: (row) => {
                        const lastOpened = moment.utc(row.value).fromNow();
                        const lastOpenedBy = row.original.lastOpenedBy ? row.original.lastOpenedBy.split("|").pop() : "";
                        const value = row.value ? `${lastOpened} - ${lastOpenedBy}` : "";

                        return WithTooltip(value);
                    },
                });

                break;
            case "status":
                selectedColumns.push({
                    Header: WithTooltip("Status"),
                    accessor: "state",
                    Cell: (row) => {
                        const state = row.value === TaskStatus.TO_DO ? "Open" : typesMap[row.value] || row.value;

                        return WithTooltip(state);
                    },
                    filterMethod: (filter, row) => {
                        if (filter.value === "all") {
                            return true;
                        }

                        return row[filter.id] === filter.value;
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => onChange(event.target.value)}
                                style={{ width: "100%" }}
                                id="select-all-filter"
                                excludeSelect={true}
                                value={(filter && filter.value) || ""}
                                options={types}
                            />
                        );
                    },
                });

                break;
            case "shipmentType":
                selectedColumns.push({
                    Header: WithIcon("Shipment Type", faBoxOpen),
                    accessor: "context.shipmentType",
                    Cell: (row) => {
                        const shipmentType = SHIPMENT_TYPE_MAP[row.original.context.shipmentType].label;

                        return WithTooltip(shipmentType);
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => onChange(event.target.value)}
                                style={{ width: "100%" }}
                                value={(filter && filter.value) || "all"}
                                id="select-type-filter"
                                options={SHIPMENT_FILTER_TYPES}
                                excludeSelect={true}
                            />
                        );
                    },
                });

                break;
            case "notes":
                selectedColumns.push({
                    Header: WithIcon("Notes", faStickyNote),
                    accessor: "note",
                    sortable: false,
                    Cell: (row) => {
                        const fullUpc = row.original.context.productModifier
                            ? `${row.original.context.upc}-${row.original.context.productModifier}`
                            : row.original.context.upc;

                        return (
                            <NoteButton
                                upc={fullUpc}
                                shipmentKey={row.original.context.shipmentKey}
                                shipmentProductKey={row.original.context.shipmentProductKey}
                                noteType="Product"
                                cxHClientCode={row.original.context.cxHClientCode}
                                binTrackingNumber={[row.original.context.binTrackingNumber]}
                                hasNotes={row.original.hasComments}
                                sourcePage={sourcePage}
                            />
                        );
                    },
                    filterable: false,
                    width: 40,
                });

                break;
            case "view":
                selectedColumns.push({
                    Header: WithIcon("View", faEye),
                    accessor: "key",
                    sortable: false,
                    Cell: (row) => {
                        return (
                            <div className="button-group">
                                <button
                                    className="button -text"
                                    title="View Task"
                                    onClick={(e) => {
                                        onNavigateClick(e, row);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEye} />
                                </button>
                            </div>
                        );
                    },
                    filterable: false,
                    width: 40,
                });

                break;
            case "assignmentGroupKey":
                selectedColumns.push({
                    Header: WithTooltip("Team Assignment"),
                    accessor: "assignmentGroupKey",
                    Cell: (row) => {
                        return (
                            <AssignmentCell
                                options={assignmentOptions}
                                row={row}
                            />
                        );
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => {
                                    const value = event.target.value;

                                    onUpdateTeamAssignments(value);
                                    onChange(event.target.value);
                                }}
                                style={{ width: "100%" }}
                                id="select-all-filter"
                                value={(filter && filter.value) || ""}
                                excludeSelect={true}
                                options={[...basicOptions, ...assignmentOptions]}
                            />
                        );
                    },
                });

                break;
            case "assignedUserInfo":
                selectedColumns.push({
                    Header: WithTooltip("User Assignments"),
                    accessor: "assignedUserInfo",
                    Cell: (row) => {
                        return WithTooltip(row.value ? row.value.fullName : "");
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <Select
                                onChange={(event) => {
                                    const value = event.target.value;

                                    onUpdateUserAssignments(value);
                                    onChange(event.target.value);
                                }}
                                style={{ width: "100%" }}
                                value={(filter && filter.value) || ""}
                                excludeSelect={true}
                                id="select-all-filter"
                                options={[...basicOptions, ...userAssignmentOptions]}
                            />
                        );
                    },
                });

                break;
            case "clientApproval":
                selectedColumns.push({
                    Header: WithIcon("ClientApproval", faUserClock),
                    accessor: userColumns[key].accessor,
                    Cell: (row) => {
                        return row.value ? <FontAwesomeIcon icon={faUserClock} /> : "";
                    },
                    filterable,
                    Filter: ({ filter, onChange }) => {
                        return (
                            <ThreeWayCheckbox
                                onChange={onChange}
                                value={filter ? filter.value : ""}
                            />
                        );
                    },
                    width: 40,
                });

                break;
            default:
                selectedColumns.push({
                    Header: WithTooltip(userColumns[key].label),
                    accessor: userColumns[key].accessor,
                    filterable,
                    Filter: inputFilter,
                    Cell: (row) => {
                        return WithTooltip(row.value);
                    },
                });
        }

        return selectedColumns;
    }, []);
};

export const basicOptions = [
    { text: "All", value: "" },
    { text: "Available", value: "null" },
];

export const taskListFilterOutputMappings = {
    trackingNumbers: "shipment.trackingNumbers",
    "context.upc": "upc",
    "context.binTrackingNumber": "binTrackingNumber",
    "context.shipmentType": "shipmentType",
    "context.hasPremiumEditing": "hasPremiumEditing",
    "context.isFoodService": "isFoodService",
    "context.imagesNeedClientApproval": "imagesNeedClientApproval",
    "context.matchedProductTaskContextKey": "matchedProductTaskContextKey",
    "locationInfo.location": "location",
    "context.packageLevelName": "packageLevelName",
    assignedUserInfo: "assignedUserInfo.userId",
};

export const advancedFiltersDateFields = {
    startCreateDate: {
        name: "startCreateDate",
        mapTo: "createDate",
        isDate: true,
        label: "From",
    },
    endCreateDate: {
        name: "endCreateDate",
        mapTo: "createDate",
        isDate: true,
        label: "To",
    },
    startUpdatedOn: {
        name: "startUpdatedOn",
        mapTo: "updatedOn",
        isDate: true,
        label: "From",
    },
    endUpdatedOn: {
        name: "endUpdatedOn",
        mapTo: "updatedOn",
        isDate: true,
        label: "To",
    },
    startShipmentDueDate: {
        name: "startShipmentDueDate",
        mapTo: "shipment.dueDate",
        isDate: true,
        label: "From",
    },
    endShipmentDueDate: {
        name: "endShipmentDueDate",
        mapTo: "shipment.dueDate",
        isDate: true,
        label: "To",
    },
};

export const advancedFiltersFields = {
    startDate: {
        name: "startDate",
        isMulti: false,
        isDate: true,
        mapTo: "due",
        label: "From",
    },
    endDate: {
        name: "endDate",
        isMulti: false,
        isDate: true,
        mapTo: "due",
        label: "To",
    },
    publishDateStart: {
        name: "publishDateStart",
        isMulti: false,
        isDate: true,
        mapTo: "context.publishDate",
        label: "From",
    },
    publishDateEnd: {
        name: "publishDateEnd",
        isMulti: false,
        isDate: true,
        mapTo: "context.publishDate",
        label: "To",
    },
    packageLevelName: {
        name: "context.packageLevelName",
        label: "Package Level",
    },
    gdnClient: {
        name: "gdnClient",
        isMulti: true,
        label: "GDN Client",
    },
    supplier: {
        name: "supplier",
        isMulti: true,
        label: "Supplier",
    },
    internalSupplierId: {
        name: "internalSupplierId",
        isMulti: true,
        label: "ISID",
    },
    shipmentKey: {
        name: "shipmentKey",
        isMulti: true,
        label: "Shipment ID",
    },
    priority: {
        name: "priority",
        isMulti: false,
        label: "Priority",
    },
    isRush: {
        name: "isRush",
        isMulti: false,
        label: "Rush",
    },
    isFoodService: {
        name: "isFoodService",
        isMulti: false,
        label: "Food Service",
    },
    clientApproval: {
        name: "context.imagesNeedClientApproval",
        label: "Client Approval",
    },
    duplicateMatch: {
        name: "context.matchedProductTaskContextKey",
        label: "Duplicate Match",
    },
    locationDataSource: {
        name: "context.locationDataSource",
        label: "Source",
    },
    hasPremiumEditing: {
        name: "context.hasPremiumEditing",
        isMulti: false,
        label: "Premium Editing",
    },
    upc: {
        name: "context.upc",
        isMulti: true,
        label: "UPC",
    },
    client: {
        name: "client",
        isMulti: false,
        label: "GDN Client",
    },
    cxhClient: {
        name: "cxhClient",
        isMulti: false,
        label: "CES Client",
    },
    binTrackingNumber: {
        name: "context.binTrackingNumber",
        isMulti: true,
        label: "Bin ID",
    },
    description: {
        name: "description",
        isMulti: false,
        label: "Task Description",
    },
    location: {
        name: "locationInfo.location",
        isMulti: false,
        label: "Location",
    },
    state: {
        name: "state",
        isMulti: false,
        label: "Status",
    },
    assignedUserInfo: {
        name: "assignedUserInfo",
        isMulti: false,
        label: "User Assignment",
    },
    assignmentGroupKey: {
        name: "assignmentGroupKey",
        isMulti: false,
        label: "Team Assignment",
    },
    shipmentType: {
        name: "context.shipmentType",
        isMulti: false,
        label: "Shipment Type",
    },
    correlationId: {},
};

export const searchAdvancedFiltersFields = {
    ...advancedFiltersDateFields,
    startDate: {
        name: "startDate",
        isMulti: false,
        isDate: true,
        mapTo: "publishDate",
        label: "From",
    },
    endDate: {
        name: "endDate",
        isMulti: false,
        isDate: true,
        mapTo: "publishDate",
        label: "To",
    },
    packageLevelName: {
        name: "packageLevelName",
        label: "Package Level",
    },
    gdnClient: {
        name: "gdnClient",
        label: "GDN Client",
    },
    supplier: {
        name: "supplier",
        isMulti: true,
        label: "Supplier",
    },
    internalSupplierId: {
        name: "internalSupplierId",
        isMulti: true,
        label: "ISID",
    },
    correlationId: {
        name: "correlationId",
        isMulti: true,
        label: "Correlation ID",
    },
    shipmentKey: {
        name: "shipment.key",
        isMulti: true,
        label: "Shipment ID",
    },
    upc: {
        name: "upc",
        isMulti: true,
        label: "UPC",
    },
    client: {
        name: "client",
        isMulti: true,
        label: "Client",
    },
    cxhClient: {
        name: "cxhClient",
        label: "CES Client",
    },
    binTrackingNumber: {
        name: "binTrackingNumber",
        isMulti: true,
        label: "Bin ID",
    },
    trackingNumbers: {
        name: "shipment.trackingNumbers",
        isMulti: true,
        label: "Tracking #",
    },
    location: {
        name: "location",
        label: "Location",
    },
    shipmentType: {
        name: "shipment.type",
        isMulti: false,
        label: "Shipment Type",
    },
    status: {
        name: "status",
        isMulti: false,
        label: "Status",
    },
    isFinalized: {
        name: "isFinalized",
        isMulti: false,
        label: "Finalized",
    },
};
